import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { CertificatePlatform } from '../types/models/CertificatePlatform';
import type { ApiResponseCertificatePlatformSingle } from '../types/api/ApiResponseCertificatePlatform';

interface UseCertificatePlatformReturn {
  platform: CertificatePlatform | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch a single certificate platform (GET /datacenters/certificate-platforms/{id}).
 *
 * Used by the certificate detail page to learn the owning platform's
 * capability flags (`support_private_key` gates the private key download and
 * tab). Pass `null` to skip fetching until the id is known (the detail page
 * only learns it once the certificate row has loaded).
 *
 * State is reset only when `platformId` itself changes — effect re-runs for
 * the same id (e.g. a token renewal changing the apiFetch identity) keep the
 * loaded platform visible while revalidating, so capability gates derived
 * from it don't flap. Late responses from a superseded run are dropped, so
 * client-side navigation between two detail pages can never gate one
 * certificate's actions on the other's platform.
 */
export const useCertificatePlatform = (platformId: number | null): UseCertificatePlatformReturn => {
  const [platform, setPlatform] = useState<CertificatePlatform | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastPlatformIdRef = useRef<number | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  useEffect(() => {
    if (lastPlatformIdRef.current !== platformId) {
      lastPlatformIdRef.current = platformId;
      setPlatform(null);
      setError(null);
    }
    if (!auth.isAuthenticated || platformId === null) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const fetchPlatform = async () => {
      try {
        const response = await apiFetch(`/datacenters/certificate-platforms/${platformId}`);
        let body: ApiResponseCertificatePlatformSingle | null;
        try {
          body = (await response.json()) as ApiResponseCertificatePlatformSingle;
        } catch {
          body = null;
        }
        if (cancelled) {
          return;
        }

        if (body?.status === 'success' && body.data?.certificate_platform) {
          setPlatform(body.data.certificate_platform);
          setError(null);
          return;
        }

        setError(
          body?.message ??
            `Failed to load certificate platform (HTTP ${`${response.status} ${response.statusText}`.trim()})`,
        );
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'An error occurred while fetching the certificate platform');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPlatform();
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, platformId, apiFetch]);

  return { platform, loading, error };
};
